import type { Role } from '@lms/shared-types'
import { flags, type FeatureFlagKey } from '../../lib/feature-flags'
import type { IconName } from '../primitives/Icon'

/**
 * Sidebar nav config.
 *
 * The sidebar mirrors the Lumen prototype's `buildNav(persona)` —
 * `prototype/app.jsx`. Everyone is an employee first; management
 * sections layer on top by role.
 *
 *   Me            (every signed-in user)
 *   Acting        (only when an EA is acting on a principal's behalf)
 *   My team       (manager + HOD)
 *   Division      (HOD + director)
 *   HR · Overview / Leave ops / Configuration / Integrations (HR admin)
 *
 * `roleAtLeast(role, item.minimumRole)` filters at render time; items
 * with a feature flag are stripped out of `NAV_ITEMS` here.
 *
 * ── Deprecated nav entries (removed in the redesign) ─────────────────
 * These items were in the legacy sidebar but the Lumen prototype does
 * not surface them. Their routes are still mounted in
 * `packages/web/src/router/routes.tsx` so deep links keep working;
 * a follow-up cleanup pass deletes the routes + pages outright.
 *
 *   My team        Dashboard          (/manager)
 *   Division       Digest             (/hod/digest)
 *                  Dashboard          (/director)
 *                  Decisions          (/director/leaves)
 *   HR · Overview  Insights           (/hr/insights)
 *                  Org chart          (/hr/org-chart → moved to Division)
 *                  Patterns           (/hr/patterns)
 *   HR · Leave ops Payroll export     (/hr/payroll)
 *   HR · Config    Peak periods       (/hr/peak-periods)
 *                  Settings           (/hr/settings)
 *
 * ── Prototype items pending pages ────────────────────────────────────
 * The prototype's sidebar references these surfaces; the live app does
 * not have pages for them yet. Add the nav entries in the same commit
 * that mounts each route.
 *
 *   My team        Team                (/manager/team — team-roster)
 *                  Features            (/hr/features)
 */
export type NavSection =
  | 'personal'
  | 'my-team'
  | 'organization'
  | 'hr-ops'
  | 'hr-config'
  | 'hr-integrations'
  | 'admin'

/**
 * Keys that `useNavCounts` can populate. Each key maps to one API-derived
 * pending-action count. Only items with a `countKey` show a badge.
 *
 * **Freshness contract (Hafiz round-1 P2 #24):**
 * - Counts are derived from React-Query caches keyed by the source list
 *   endpoint, NOT a dedicated `/count` route. See
 *   `packages/web/src/lib/use-nav-counts.ts` for the per-key mapping.
 * - Cache invalidation happens on:
 *     1. Mutation success (approve/reject/escalate/cancel) — the page
 *        that triggers the action invalidates its own list key and
 *        the badge re-derives on the next render.
 *     2. Window refocus + 60s stale time — React-Query default for
 *        the underlying list queries; managers returning to the tab
 *        after lunch see a fresh badge.
 *     3. Route navigation into the badged surface — the page mount
 *        fires a fresh fetch.
 * - There is NO websocket / SSE / cron poll today. If a request lands
 *   at 6pm while you're on `/calendar`, the `/manager` badge won't tick
 *   up until you focus the tab or navigate.
 */
export type NavCountKey =
  | 'manager-requests'
  | 'wfh-approvals'

/** Sparse map of countKey → current pending count. Populated by `useNavCounts`. */
export type NavCounts = Partial<Record<NavCountKey, number>>

export interface NavItem {
  label: string
  to: string
  minimumRole: Role
  section: NavSection
  /** Optional feature flag. Item is omitted from the sidebar when
   *  the flag is false. */
  flag?: FeatureFlagKey
  /** Material Symbols (rounded) name rendered as the leading icon in
   *  expanded mode and as the sole glyph in the collapsed icon-rail. */
  icon: IconName
  /**
   * When set, `useNavCounts` will populate a live pending-action badge for
   * this nav item. The badge appears as a count chip in expanded mode and
   * a small dot in collapsed mode.
   */
  countKey?: NavCountKey
  /**
   * Hide this nav item once the viewer's role hits this floor (exclusive).
   *
   * Mirrors `minimumRole` but at the upper bound — lets us specialise a
   * shared link by role without duplicating it. Example: `My team` items
   * use `hiddenForRoleAtLeast: 'director'` so a Director reaches their
   * Division section instead.
   *
   * NOTE: unlike `minimumRole` and `excludeForRoles` (which evaluate against
   * the user's FULL role set), this ceiling is evaluated against the
   * PRIMARY/effective single role — it expresses "what I'm acting as is too
   * senior for this link", not "any of my roles is too senior". No nav item
   * sets this today, so the asymmetry is currently inert; a future author
   * adding one should be aware it does NOT consider the role set.
   */
  hiddenForRoleAtLeast?: Role
  /**
   * Explicit blacklist of roles that must not see this item.
   *
   * Required because the role hierarchy is non-linear — `hr` (rank 3)
   * sits between `hod` (2) and `director` (4). A simple `minimumRole`
   * + `hiddenForRoleAtLeast` pair can't express "HOD and Director, but
   * not HR" without a blacklist. Used on `My team` and `Division`
   * sections so an HR admin doesn't see the manager / division
   * surfaces (they have their own HR sub-sections).
   */
  excludeForRoles?: ReadonlyArray<Role>
}

export const NAV_SECTION_ORDER: ReadonlyArray<NavSection> = [
  'personal',
  'my-team',
  'hr-ops',
  'hr-config',
  'organization',
  'hr-integrations',
  'admin',
]

/** Section header text. */
export const NAV_SECTION_LABEL: Record<NavSection, string> = {
  personal: 'Me',
  'my-team': 'My team',
  organization: 'Organization',
  'hr-ops': 'HR Ops',
  'hr-config': 'HR · Configuration',
  'hr-integrations': 'Notifications',
  admin: 'Admin',
}

export const ALL_NAV_ITEMS: ReadonlyArray<NavItem> = [
  // ── Me (everyone) ──────────────────────────────────────────────
  { section: 'personal', label: 'Home',          to: '/',         minimumRole: 'employee', icon: 'home' },
  { section: 'personal', label: 'Apply leave',   to: '/apply',    minimumRole: 'employee', icon: 'edit_calendar' },
  { section: 'personal', label: 'My leaves',     to: '/my-leave', minimumRole: 'employee', icon: 'event_note' },
  { section: 'personal', label: 'Work location', to: '/wfh',     minimumRole: 'employee', flag: 'wfhRequest', icon: 'home_work', countKey: 'wfh-approvals' },
  { section: 'personal', label: 'Calendar',     to: '/calendar', minimumRole: 'employee', icon: 'calendar_month' },

  // ── My team (manager + HOD + Director + HR) ────
  // Directors are first-line approvers for their own direct reports too,
  // so they get the My team section alongside their Division surfaces.
  // `minimumRole: 'manager'` admits manager/hod/hr/director/exec_director;
  // HR is intentionally included here (was previously excluded) so HR
  // admins can reach the manager surfaces directly.
  { section: 'my-team', label: 'Team',       to: '/manager/team',       minimumRole: 'manager', icon: 'groups' },
  { section: 'my-team', label: 'Approvals',  to: '/manager/requests',   minimumRole: 'manager', icon: 'inbox',           countKey: 'manager-requests' },
  // Manager Patterns — same operational board as /hr/patterns; the api
  // RBAC-scopes the response to the manager's direct reports.
  { section: 'my-team', label: 'Rhythm',     to: '/manager/patterns',   minimumRole: 'manager', icon: 'query_stats' },

  // ── Department overview retired ─────────────────────────────────
  // The standalone Escalations queue folded into Approvals (My team ·
  // Approvals → "For approvals" tab), so Director/Exec reach escalated-to-them
  // leaves there. The Department → Overview page was retired — its stats
  // (headcount, on-leave-this-week, next holiday) now live on the Team page.

  // ── Organization ──────────────────────────────────────────────
  // The read-only chart (view) + the structure admin (manage). Team CRUD
  // lives inside Structure's "Teams" tab, so there's no separate Teams
  // entry. Chart is scoped to HR + Executive Director only: `minimumRole:
  // 'hr'` drops employee/manager/hod, and `excludeForRoles: ['director']`
  // removes Director (rank 4, above hr) while keeping Exec Director (rank
  // 5). Structure stays hr-gated. HOD/Director therefore see no
  // Organization section.
  { section: 'organization', label: 'Chart',            to: '/hr/org-chart',     minimumRole: 'hr', flag: 'hrOrgChart',        icon: 'account_tree', excludeForRoles: ['director'] },
  { section: 'organization', label: 'Manage structure', to: '/hr/org-structure', minimumRole: 'hr',  flag: 'orgStructureAdmin', icon: 'lan', excludeForRoles: ['director'] },

  // ── HR Ops ─────────────────────────────────────────────────────
  // The former "HR · Overview" + "HR · Leave ops" sections, merged. No
  // 'Overview' item — the HR dashboard is the role's Home (Me · Home → /,
  // HrHomeDashboard). The balance-affecting actions live under the "Leaves"
  // hub (Applications / Balances / Entitlement / Replacement Grants tabs).
  // Import & Export is one sidebar entry with in-page tabs; Delegations stands
  // alone.
  { section: 'hr-ops', label: 'Employees',      to: '/hr/employees',     minimumRole: 'hr', flag: 'hrEmployees',      icon: 'groups' },
  { section: 'hr-ops', label: 'Leaves',         to: '/hr/leaves',        minimumRole: 'hr', flag: 'hrLeaves',         icon: 'event_note' },
  // Attendance register — the prototype's HR view of every WFH / out-on-
  // business record, all teams. Gated by its own kill-switch flag.
  { section: 'hr-ops', label: 'Attendance', to: '/wfh/register', minimumRole: 'hr', flag: 'wfhRegister', icon: 'fact_check' },
  { section: 'hr-ops', label: 'Audit log',      to: '/hr/audit',         minimumRole: 'hr', flag: 'hrAudit',          icon: 'receipt_long' },
  { section: 'hr-ops', label: 'Rhythm',         to: '/hr/patterns',      minimumRole: 'hr', icon: 'query_stats' },
  { section: 'hr-ops', label: 'Import & Export', to: '/hr/import-export', minimumRole: 'hr', flag: 'hrImportExportHub', icon: 'import_export' },
  { section: 'hr-ops', label: 'Delegations',    to: '/hr/approver-cover', minimumRole: 'hr', flag: 'approverOverride', icon: 'supervisor_account' },

  // ── HR · Configuration ─────────────────────────────────────────
  { section: 'hr-config', label: 'Blackouts',       to: '/hr/blackouts',       minimumRole: 'hr', flag: 'hrBlackouts',    icon: 'shield' },
  { section: 'hr-config', label: 'Holidays',        to: '/hr/holidays',        minimumRole: 'hr', flag: 'hrHolidays',     icon: 'celebration' },
  { section: 'hr-config', label: 'Coverage thresholds', to: '/hr/coverage', minimumRole: 'hr', flag: 'teamCoverageHrControl', icon: 'equalizer' },

  // ── Notifications (formerly HR · Integrations) ────────────────
  { section: 'hr-integrations', label: 'Slack',       to: '/hr/slack', minimumRole: 'hr', icon: 'chat' },
  { section: 'hr-integrations', label: 'Email',       to: '/hr/email', minimumRole: 'hr', flag: 'emailSettings', icon: 'mail' },
  { section: 'hr-integrations', label: 'Calendar',    to: '/hr/calendar', minimumRole: 'hr', flag: 'hrCalendarConnector', icon: 'calendar_month' },

  // ── Admin (white-label admin console; admin / superadmin only) ────
  {
    section: 'admin',
    label: 'Admin console',
    to: '/admin',
    minimumRole: 'admin',
    icon: 'admin_panel_settings',
    flag: 'adminHub',
  },
  // Branding & Theme — superadmin-only white-label surface (company identity,
  // logo/favicon uploads, colour theme). Own page + gate, not an Admin-console tab.
  {
    section: 'admin',
    label: 'Branding & Theme',
    to: '/admin/branding',
    minimumRole: 'superadmin',
    icon: 'palette',
    flag: 'adminHub',
  },
  // Background jobs — admin+ runtime control for managed crons + weekly digest
  // notifications. Own sidebar entry (not an Admin-console tab) for
  // discoverability. Same admin-level gate as the rest of the admin area.
  {
    section: 'admin',
    label: 'Background jobs',
    to: '/admin/jobs',
    minimumRole: 'admin',
    icon: 'schedule',
    flag: 'adminBackgroundJobs',
  },
]

export const NAV_ITEMS: ReadonlyArray<NavItem> = ALL_NAV_ITEMS.filter(
  (item) => item.flag == null || flags[item.flag],
)

/**
 * Resolve which role + role-set the sidebar should render for, accounting for
 * "Viewing as" (HR read-only) impersonation. The nav reflects the impersonated
 * identity's access — an HR user viewing as a manager sees the principal's
 * sections (e.g. "My team").
 *
 *   - `effectiveRole` already folds in "Viewing as".
 *
 * When view-as is active the nav is gated against the single impersonated role;
 * otherwise the real user's full role set is used so multi-role users
 * (e.g. hr+manager) keep seeing items for all their roles.
 */
export function resolveNavIdentity(args: {
  realRole: Role
  realRoles: Role[]
  effectiveRole: Role
}): { navRole: Role; navRoles: Role[] } {
  const { realRole, realRoles, effectiveRole } = args
  const navRole: Role = effectiveRole !== realRole ? effectiveRole : realRole
  const navRoles: Role[] = navRole !== realRole ? [navRole] : realRoles
  return { navRole, navRoles }
}
